import { DndContext, DragEndEvent } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { Button, Icon, Tag, Popover, PopoverList } from '@workspace/design-system/src/components';

import type { PricingTier } from '@src/hooks/template/utils';

interface TemplateItem {
    id: string;
    order: number;
    imageUrl: string;
    name: string;
    description: string;
    pricingTiers: PricingTier[];
    studioUrl?: string;
}

interface TemplateTableProps {
    data: TemplateItem[];
    isLoading?: boolean;
    onDragEnd?: (event: DragEndEvent) => void;
    onPricingTierUpdate?: (id: string, tiers: PricingTier[]) => void;
    onStudioOpen?: (id: string) => void;
    onSettingClick?: (item: TemplateItem) => void;
    onContentClick?: (item: TemplateItem) => void;
}

const tierLabels: Record<PricingTier, string> = {
    ALL: '무료',
    PRO: '프로',
    ENTERPRISE: '엔터프라이즈',
    TEACHER: '티처',
    CLASS: '클래스'
};

interface SortableRowProps {
    item: TemplateItem;
    onSettingClick: (item: TemplateItem) => void;
    onContentClick: (item: TemplateItem) => void;
    onStudioOpen?: (id: string) => void;
}

function SortableRow({ item, onSettingClick, onContentClick, onStudioOpen }: SortableRowProps) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: item.id
    });

    const style: React.CSSProperties = {
        transform: CSS.Transform.toString(transform && { ...transform, scaleY: 1 }),
        transition,
        ...(isDragging ? { position: 'relative', zIndex: 1000 } : {})
    };

    return (
        <tr ref={setNodeRef} style={style} {...attributes} className="bg-background border-b border-b-border-divider">
            {/* 순서 - 드래그 핸들 */}
            <td className="px-4 py-2.5 text-center">
                <div className="flex items-center justify-center">
                    <div
                        {...listeners}
                        className="cursor-move w-10 h-10 flex items-center justify-center hover:bg-surface-gray-tint rounded-full"
                    >
                        <Icon name="Menu" size={24} color="var(--icon-icon-default)" />
                    </div>
                </div>
            </td>

            {/* 이미지 */}
            <td className="px-4 py-2.5">
                <div className="flex items-center">
                    <div className="w-10 h-[30px] rounded overflow-hidden">
                        <img
                            src={item.imageUrl}
                            alt={item.name}
                            className="w-full h-full object-cover"
                        />
                    </div>
                </div>
            </td>

            {/* 콘텐츠명 */}
            <td className="px-4 py-2.5 flex-1">
                <div className="flex items-center gap-1">
                    <button
                        onClick={() => onContentClick(item)}
                        className="text-size-6 font-bold leading-font-height-6 text-text-blue underline truncate cursor-pointer hover:opacity-80"
                    >
                        {item.name}
                    </button>
                </div>
            </td>

            {/* 콘텐츠 한 줄 설명 */}
            <td className="px-4 py-2.5 flex-1">
                <span className="text-size-6 font-regular leading-font-height-5 text-text-default tracking-[-0.1px] truncate">
                    {item.description}
                </span>
            </td>

            {/* 사용 가능 요금제 */}
            <td className="px-4 py-2.5 w-[214px]">
                <div className="flex items-center gap-2">
                    {item.pricingTiers.length > 0 &&
                        item.pricingTiers.map((tier) => (
                            <Tag
                                key={tier}
                                text={tierLabels[tier]}
                                color="primary"
                                size="S-16"
                            />
                        ))}
                </div>
            </td>

            {/* 설정 버튼 */}
            <td className="px-4 py-2.5">
                <div className="flex items-center gap-1">
                    <Button
                        styleType="basic"
                        styleSize="S-32"
                        onClick={() => onSettingClick(item)}
                    >
                        설정
                    </Button>
                </div>
            </td>

            {/* 이동 - 케밥 메뉴 */}
            <td className="px-4 py-2.5">
                <div className="flex items-center justify-center">
                    <Popover
                        element={
                            <button className="w-10 h-10 flex items-center justify-center hover:bg-surface-gray-tint rounded-full">
                                <Icon name="KebabFilled" size={24} color="var(--icon-icon-default)" />
                            </button>
                        }
                        content={
                            <PopoverList
                                text="스튜디오 열기"
                                onClick={() => onStudioOpen?.(item.id)}
                            />
                        }
                    />
                </div>
            </td>
        </tr>
    );
}

export default function TemplateTable({
    data,
    isLoading,
    onDragEnd,
    onPricingTierUpdate,
    onStudioOpen,
    onSettingClick,
    onContentClick
}: TemplateTableProps) {
    const handleDragEnd = (event: DragEndEvent) => {
        if (onDragEnd) {
            onDragEnd(event);
        }
    };

    const handleSettingClick = (item: TemplateItem) => {
        if (onSettingClick) {
            onSettingClick(item);
        }
    };

    const handleContentClick = (item: TemplateItem) => {
        if (onContentClick) {
            onContentClick(item);
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-12">
                <span className="text-text-subdued">로딩 중...</span>
            </div>
        );
    }

    return (
        <div className="overflow-hidden">
            <DndContext modifiers={[restrictToVerticalAxis]} onDragEnd={handleDragEnd}>
                <SortableContext items={data.map((i) => i.id)} strategy={verticalListSortingStrategy}>
                    <table className="w-full">
                        <thead className="bg-surface-section">
                            <tr>
                                <th className="px-4 py-2.5 text-size-6 font-bold leading-font-height-6 text-text-default text-center w-[72px]">
                                    순서
                                </th>
                                <th className="px-4 py-2.5 text-size-6 font-bold leading-font-height-6 text-text-default text-left w-[72px]">
                                    이미지
                                </th>
                                <th className="px-4 py-2.5 text-size-6 font-bold leading-font-height-6 text-text-default text-left flex-1">
                                    콘텐츠명
                                </th>
                                <th className="px-4 py-2.5 text-size-6 font-bold leading-font-height-6 text-text-default text-left flex-1">
                                    콘텐츠 한 줄 설명
                                </th>
                                <th className="px-4 py-2.5 text-size-6 font-bold leading-font-height-6 text-text-default text-left w-[295px]">
                                    사용 가능 요금제
                                </th>
                                <th className="px-4 py-2.5 w-[80px]"></th>
                                <th className="px-4 py-2.5 text-size-6 font-bold leading-font-height-6 text-text-default text-center w-[72px]">
                                    이동
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.map((item) => (
                                <SortableRow
                                    key={item.id}
                                    item={item}
                                    onSettingClick={handleSettingClick}
                                    onContentClick={handleContentClick}
                                    onStudioOpen={onStudioOpen}
                                />
                            ))}
                        </tbody>
                    </table>
                </SortableContext>
            </DndContext>
        </div>
    );
}

